/**
 * SwmsPdfExportModal.js — Batch PDF export modal for SWMS.
 *
 * Adapted from SWMS Platform BatchPdfExportModal.tsx → JavaScript.
 *
 * Flow:
 *   1. User selects chapters → clicks Export
 *   2. Progress overlay shows (modal body stays mounted)
 *   3. Each selected chapter's [data-pdf-page] elements are captured
 *      via html2canvas → jsPDF (single merged document)
 *   4. Download / Open in Tab buttons appear
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { X, Check, Loader2, Download, ExternalLink, FileText, FolderOpen } from 'lucide-react';

// ── Chapter catalog (matches SwmsPdfPreview rendering) ───────────────────────

const OHSE_CHAPTERS = [
  // Cover — always included
  { code: 'cover', title: 'Cover Page', category: 'cover' },
  { code: 'toc', title: 'Table of Contents', category: 'cover' },
  // Document Control
  { code: '001', title: 'Document Control', category: 'document-control' },
  // Company Info
  { code: '002', title: 'Project Details & Introduction', category: 'company-info' },
  { code: '003', title: 'OHSE & Environment Policy', category: 'company-info' },
  // Risk Assessment
  { code: '004', title: 'Hazard Identification & Risk Assessment', category: 'risk' },
  { code: '005', title: 'Hazard Categories', category: 'risk' },
  { code: '006', title: 'Risk Matrix', category: 'risk' },
  // SWMS
  { code: '007', title: 'Safe Work Method Statement', category: 'swms' },
  { code: '008', title: 'Objectives & Targets', category: 'swms' },
  // PPE
  { code: '009', title: 'Personal Protective Equipment', category: 'ppe-equipment' },
  // Training
  { code: '010', title: 'Roles & Responsibilities', category: 'training' },
  { code: '011', title: 'Training & Competency Register', category: 'training' },
  { code: '012', title: 'Consultation & Communication', category: 'training' },
  { code: '013', title: 'Toolbox Talks / Pre-Start Briefings', category: 'training' },
  { code: '014', title: 'Workplace Inspection Checklist', category: 'training' },
  // Hazard Management
  { code: '015', title: 'Plant & Equipment Policy', category: 'hazard-management' },
  { code: '016', title: 'Plant & Equipment Register', category: 'hazard-management' },
  { code: '017', title: 'Plant & Equipment Pre-Start Inspection', category: 'hazard-management' },
  { code: '018', title: 'Plant & Equipment Regular Checklist', category: 'hazard-management' },
  { code: '019', title: 'Hazardous Substances / Dangerous Goods Policy', category: 'hazard-management' },
  { code: '020', title: 'Hazardous Substance Register', category: 'hazard-management' },
  { code: '021', title: 'Electrical Equipment Policy', category: 'hazard-management' },
  { code: '022', title: 'Electrical Equipment Register', category: 'hazard-management' },
  { code: '023', title: 'Hazard Reporting', category: 'hazard-management' },
  { code: '024', title: 'Hazard Report Form', category: 'hazard-management' },
  // Incident Management
  { code: '025', title: 'Incident & Injury Investigation Policy', category: 'incident' },
  { code: '026', title: 'Register of Injuries', category: 'incident' },
  { code: '027', title: 'Incident Investigation Report', category: 'incident' },
  { code: '028', title: 'OHSE Management Plan Checklist', category: 'incident' },
  { code: '029', title: 'Injury Management & Return to Work', category: 'incident' },
  // Site Rules
  { code: '030', title: 'Daily Sign In / Sign Out', category: 'site-rules' },
  { code: '031', title: 'Site Rules & Emergency Contacts', category: 'site-rules' },
];

const CATEGORIES = {};
OHSE_CHAPTERS.forEach(ch => {
  if (!CATEGORIES[ch.category]) CATEGORIES[ch.category] = { label: ch.category, chapters: [] };
  CATEGORIES[ch.category].chapters.push(ch);
});
// Human-readable category labels
const CAT_LABELS = {
  'cover': 'Cover',
  'document-control': 'Document Control',
  'company-info': 'Company Information',
  'risk': 'Risk Assessment',
  'swms': 'Safe Work Method Statement',
  'ppe-equipment': 'PPE & Equipment',
  'training': 'Training & Inspection',
  'hazard-management': 'Hazard Management',
  'incident': 'Incident Management',
  'site-rules': 'Site Rules',
};

// ── Modal component ──────────────────────────────────────────────────────────

export default function SwmsPdfExportModal({ open, onClose, projectName }) {
  const [selectedCodes, setSelectedCodes] = useState(new Set(['cover', 'toc', '002', '007', '009', '005'])); // sensible defaults
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, chapter: '' });
  const [exportPhase, setExportPhase] = useState('idle'); // idle | capturing | done
  const [error, setError] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');

  const blobUrlRef = useRef(null);
  const startTimeRef = useRef(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Elapsed timer
  useEffect(() => {
    if (!exporting) { setElapsedSeconds(0); return; }
    const id = setInterval(() => {
      if (startTimeRef.current > 0) setElapsedSeconds((Date.now() - startTimeRef.current) / 1000);
    }, 500);
    return () => clearInterval(id);
  }, [exporting]);

  const handleClose = useCallback(() => {
    if (exporting) return;
    setSelectedCodes(new Set(['cover', 'toc', '002', '007', '009', '005']));
    setError(null);
    setExportPhase('idle');
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setPdfBlobUrl(null);
    onClose();
  }, [exporting, onClose]);

  const toggleChapter = useCallback((code) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((catKey) => {
    const cat = CATEGORIES[catKey];
    if (!cat) return;
    const codes = cat.chapters.map(c => c.code);
    setSelectedCodes(prev => {
      const next = new Set(prev);
      const allSelected = codes.every(c => next.has(c));
      codes.forEach(c => allSelected ? next.delete(c) : next.add(c));
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const allCodes = OHSE_CHAPTERS.map(c => c.code);
    setSelectedCodes(prev => {
      const allSelected = allCodes.every(c => prev.has(c));
      return allSelected ? new Set() : new Set(allCodes);
    });
  }, []);

  // ── Main export handler ────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (selectedCodes.size === 0) return;

    const codesToExport = [...selectedCodes];
    setExporting(true);
    setError(null);
    setExportPhase('capturing');
    setProgress({ current: 0, total: codesToExport.length, chapter: 'Preparing…' });
    startTimeRef.current = Date.now();

    try {
      // Locate the PDF preview container
      const previewEl = document.getElementById('swms-pdf-preview');
      if (!previewEl) throw new Error('PDF preview element not found. Please reload the page.');

      const { capturePdfPages } = await import('./PdfPage');

      // Capture chapters one at a time; progress updates after each chapter completes
      const blob = await capturePdfPages(previewEl, codesToExport, (done, total, code) => {
        const ch = OHSE_CHAPTERS.find(c => c.code === code);
        const label = ch ? `${code} — ${ch.title}` : code;
        setProgress({ current: done, total, chapter: label });
      });

      const fileName = `${projectName || 'SWMS'}_OHSE_Management_Plan.pdf`;
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;

      setPdfBlobUrl(url);
      setPdfFileName(fileName);
      setExportPhase('done');
      setExporting(false);
      setProgress({ current: codesToExport.length, total: codesToExport.length, chapter: '' });
    } catch (err) {
      console.error('[SWMS Export] Error:', err);
      setError(err.message || 'Export failed');
      setExporting(false);
      setExportPhase('idle');
    }
  }, [selectedCodes, projectName]);

  // ── Download / Open ────────────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    const url = blobUrlRef.current;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = pdfFileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 200);
  }, [pdfFileName]);

  const handleOpenInTab = useCallback(() => {
    const url = blobUrlRef.current;
    if (!url) return;
    window.open(url, '_blank');
  }, []);

  if (!open) return null;

  const allCodes = OHSE_CHAPTERS.map(c => c.code);
  const allSelected = allCodes.every(c => selectedCodes.has(c));

  const formatTime = (s) => {
    if (s < 1) return '';
    if (s < 60) return `~${Math.ceil(s)}s left`;
    return `~${Math.floor(s / 60)}m ${Math.ceil(s % 60)}s left`;
  };

  const avgTimePerChapter = progress.current > 0 && elapsedSeconds > 0
    ? elapsedSeconds / progress.current : 2;
  const estimatedRemaining = exportPhase === 'capturing'
    ? (progress.total - progress.current) * avgTimePerChapter : 0;

  return (
    <>
      {/* ── Floating progress overlay ─────────────────────────────────── */}
      {exporting && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: '#1a1a2e', color: 'white', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          padding: '16px 20px', width: 380,
          border: '1px solid rgba(204,180,110,0.2)',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(204,180,110,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FileText size={16} color="#ccb46e" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Generating PDF
              </p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {progress.chapter}
              </p>
            </div>
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#ccb46e' }}>
              {progress.current}/{progress.total}
            </span>
          </div>

          <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
              background: 'linear-gradient(90deg, #ccb46e, #1a1a2e)',
              transition: 'width 0.5s ease-out',
            }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
              {elapsedSeconds > 0 && `${elapsedSeconds.toFixed(0)}s elapsed`}
            </span>
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)' }}>
              {formatTime(estimatedRemaining)}
            </span>
          </div>

          {/* Chapter dots */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
            {OHSE_CHAPTERS.filter(c => selectedCodes.has(c.code)).map((ch, idx) => {
              const isDone = idx < progress.current;
              const isCurrent = idx === progress.current - 1 && exportPhase === 'capturing';
              return (
                <div key={ch.code} title={ch.title} style={{
                  width: 20, height: 20, borderRadius: 4,
                  fontSize: 8, fontFamily: 'monospace',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isDone ? '#ccb46e' : isCurrent ? 'rgba(204,180,110,0.3)' : 'rgba(255,255,255,0.05)',
                  color: isDone ? '#1a1a2e' : isCurrent ? '#ccb46e' : 'rgba(255,255,255,0.2)',
                  outline: isCurrent ? '1px solid rgba(204,180,110,0.5)' : 'none',
                  transform: isCurrent ? 'scale(1.1)' : 'scale(1)',
                  transition: 'all 0.3s',
                }}>
                  {isDone ? '✓' : ch.code.replace(/^0+/, '')}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Modal ─────────────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 5000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Backdrop */}
        <div onClick={handleClose} style={{
          position: 'absolute', inset: 0, background: 'rgba(26,26,46,0.6)', backdropFilter: 'blur(4px)',
        }} />

        {/* Dialog */}
        <div style={{
          position: 'relative', background: '#f8f7f4', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)', width: 560, maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          fontFamily: "'Inter', system-ui, sans-serif",
          margin: 16,
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 24px', borderBottom: '1px solid #e0dbd0',
          }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Batch PDF Export
              </h2>
              <p style={{ fontSize: 12, color: '#6b6560', marginTop: 2 }}>
                {exportPhase === 'done'
                  ? 'Export complete!'
                  : `${selectedCodes.size} of ${OHSE_CHAPTERS.length} chapters selected`}
              </p>
            </div>
            <button onClick={handleClose} disabled={exporting} style={{
              padding: 6, borderRadius: 8, border: 'none', background: 'none',
              cursor: exporting ? 'not-allowed' : 'pointer', color: '#6b6560',
              opacity: exporting ? 0.5 : 1,
            }}>
              <X size={20} />
            </button>
          </div>

          {/* Success state */}
          {exportPhase === 'done' && pdfBlobUrl && (
            <div style={{ padding: '32px 24px', textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#d1fae5', display: 'flex', alignItems: 'center',
                justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth={2.5}>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>PDF Ready!</h3>
              <p style={{ fontSize: 13, color: '#6b6560', marginBottom: 24 }}>{pdfFileName}</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button onClick={handleDownload} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#ccb46e', color: '#1a1a2e', fontWeight: 600,
                  fontSize: 13, cursor: 'pointer',
                }}>
                  <Download size={16} /> Download PDF
                </button>
                <button onClick={handleOpenInTab} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 8, border: '1px solid #e0dbd0',
                  background: 'white', color: '#1a1a2e', fontWeight: 500,
                  fontSize: 13, cursor: 'pointer',
                }}>
                  <ExternalLink size={16} /> Open in Tab
                </button>
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
                Click Download to save, or Open in Tab to preview first.
              </p>
            </div>
          )}

          {/* Chapter selection */}
          {exportPhase !== 'done' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {/* Select All */}
              <button onClick={toggleAll} disabled={exporting} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', borderRadius: 8, border: `1px solid ${allSelected ? '#ccb46e' : '#e0dbd0'}`,
                background: allSelected ? 'rgba(204,180,110,0.1)' : 'transparent',
                cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.6 : 1,
                fontSize: 13, fontWeight: 600, color: allSelected ? '#ccb46e' : '#6b6560',
                letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 16,
              }}>
                <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
                <div style={{
                  width: 20, height: 20, borderRadius: 4,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `2px solid ${allSelected ? '#ccb46e' : '#c9c4b8'}`,
                  background: allSelected ? '#ccb46e' : 'transparent',
                }}>
                  {allSelected && <Check size={12} color="white" strokeWidth={3} />}
                </div>
              </button>

              {/* Categories */}
              {Object.entries(CATEGORIES).map(([catKey, cat]) => {
                const catCodes = cat.chapters.map(c => c.code);
                const catSelectedCount = catCodes.filter(c => selectedCodes.has(c)).length;
                const catAllSelected = catCodes.length > 0 && catCodes.every(c => selectedCodes.has(c));

                return (
                  <div key={catKey} style={{ marginBottom: 8 }}>
                    <button onClick={() => toggleCategory(catKey)} disabled={exporting} style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderRadius: 6, border: 'none', background: 'none',
                      cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.6 : 1,
                      fontSize: 11, fontWeight: 600, color: catAllSelected ? '#ccb46e' : catSelectedCount > 0 ? '#1a1a2e' : '#6b6560',
                      letterSpacing: 0.5, textTransform: 'uppercase',
                    }}>
                      <span>{CAT_LABELS[catKey] || catKey}</span>
                      <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#6b6560' }}>
                        {catSelectedCount}/{catCodes.length}
                      </span>
                    </button>

                    <div style={{ marginLeft: 8, marginTop: 2 }}>
                      {cat.chapters.map(ch => {
                        const isSelected = selectedCodes.has(ch.code);
                        return (
                          <div
                            key={ch.code}
                            role="button"
                            tabIndex={exporting ? -1 : 0}
                            onClick={() => { if (!exporting) toggleChapter(ch.code); }}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && !exporting) {
                                e.preventDefault(); toggleChapter(ch.code);
                              }
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '6px 12px', borderRadius: 6,
                              cursor: exporting ? 'not-allowed' : 'pointer',
                              opacity: exporting ? 0.6 : 1,
                              background: isSelected ? 'rgba(204,180,110,0.08)' : 'transparent',
                              transition: 'background 0.15s',
                            }}
                          >
                            <div style={{
                              width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: `2px solid ${isSelected ? '#ccb46e' : '#c9c4b8'}`,
                              background: isSelected ? '#ccb46e' : 'transparent',
                              transition: 'all 0.15s',
                            }}>
                              {isSelected && <Check size={10} color="white" strokeWidth={3} />}
                            </div>
                            <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b6560', width: 32, flexShrink: 0 }}>
                              {ch.code}
                            </span>
                            <span style={{ fontSize: 13, color: '#1a1a2e', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ch.title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '8px 24px', background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
              <p style={{ fontSize: 12, color: '#dc2626' }}>{error}</p>
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 24px', borderTop: '1px solid #e0dbd0',
          }}>
            <button onClick={handleClose} disabled={exporting} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', background: 'none',
              cursor: exporting ? 'not-allowed' : 'pointer', fontSize: 13,
              color: '#6b6560', opacity: exporting ? 0.5 : 1,
            }}>
              {exportPhase === 'done' ? 'Close' : 'Cancel'}
            </button>
            {exportPhase !== 'done' && (
              <button
                onClick={handleExport}
                disabled={exporting || selectedCodes.size === 0}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: exporting || selectedCodes.size === 0 ? '#c9c4b8' : '#ccb46e',
                  color: exporting || selectedCodes.size === 0 ? '#6b6560' : '#1a1a2e',
                  fontWeight: 600, fontSize: 13, cursor: exporting || selectedCodes.size === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {exporting ? (
                  <><Loader2 size={16} className="animate-spin" /> Exporting…</>
                ) : (
                  <><FolderOpen size={16} /> Export {selectedCodes.size > 0 ? `${selectedCodes.size} Chapters` : 'PDF'}</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
